import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { TourProvider } from './context/TourContext';
import { HelpCenterProvider } from './context/HelpCenterContext';
import { AnalyticsFilterProvider } from '../context/AnalyticsFilterContext';
import { TopBarLeftContentProvider } from './context/TopBarLeftContentContext';
import { MobileFilterProvider } from './context/MobileFilterContext';
import { MobileTopNavBarProvider, useMobileTopNavBar } from './context/MobileTopNavBarContext';
import Sidebar from './Sidebar';
import GrowthPartnerDock from './GrowthPartnerDock';
import AdminDock from './AdminDock';
import FloatingBrandSelector from './BrandSelector/FloatingBrandSelector';
import MobileTopNavBar from './MobileTopNavBar';
import AppTour from './AppTour';

const ProtectedLayout = () => {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(256); // Ancho inicial del sidebar

  const [campaigns] = useState([]);
  const [loading] = useState(false);
  const [error] = useState(null);

  // Función para manejar los cambios de estado del sidebar
  const handleSidebarStateChange = (sidebarState) => {
    setSidebarWidth(sidebarState.width);
  };

  const onboardingCompleted = auth?.user?.onboarding_completed;
  const verifySession = useCallback(async () => {
    try {
      if (!onboardingCompleted) {
        navigate('/onboarding');
        return;
      }
    } catch (err) {
      console.error('Error en verificación de sesión:', err);
      logout();
      navigate('/login');
    } finally {
      setIsVerifying(false);
    }
  }, [onboardingCompleted, navigate, logout]);

  useEffect(() => {
    if (!auth.token) {
      navigate('/login');
      return;
    }
    verifySession();
  }, [auth.token, navigate, verifySession]);

  const effectiveSidebarWidth = typeof window !== 'undefined' && window.innerWidth >= 1024 ? sidebarWidth : 0;

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const sidebarWidthValue = `${effectiveSidebarWidth}px`;
    document.documentElement.style.setProperty('--sidebar-width', sidebarWidthValue);
    document.body.style.setProperty('--sidebar-width', sidebarWidthValue);

    return () => {
      document.documentElement.style.setProperty('--sidebar-width', '0px');
      document.body.style.setProperty('--sidebar-width', '0px');
    };
  }, [effectiveSidebarWidth]);

  if (isVerifying) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!auth.token) {
    return <Navigate to="/login" replace />;
  }

  // Cuenta demo (Opus): solo ve el chat /demo-opus (su propio shell, sin sidebar).
  // Cualquier ruta protegida la devuelve ahí, para no exponer el resto del panel.
  if (auth?.user?.is_demo) {
    return <Navigate to="/demo-opus" replace />;
  }

  // Forus (piloto de eventos): la org solo ve /eventos, no el resto del panel.
  const EVENT_ONLY_ORGS = [1577];
  if (EVENT_ONLY_ORGS.includes(auth?.user?.organization_id) && !location.pathname.startsWith('/eventos')) {
    return <Navigate to="/eventos" replace />;
  }

  // Growth partner: solo puede ver su panel y su perfil (invitaciones).
  // Cualquier otra ruta lo devuelve al panel. La API además lo bloquea por su cuenta.
  const isGrowthPartner = Boolean(auth?.user?.is_growth_partner);
  const isAdmin = Boolean(auth?.user?.is_admin);
  // GP usa el dock flotante (no el sidebar): el main deja un margen chico para
  // despejar el dock en desktop; en mobile el dock flota encima.
  const gpDesktop = typeof window !== 'undefined' && window.innerWidth >= 640;
  const sidebarMargin = isGrowthPartner ? (gpDesktop ? 100 : 0) : effectiveSidebarWidth;
  const growthPartnerAllowedRoute = ['/mi-panel', '/profile', '/admin/bebo'].some((p) =>
    location.pathname.startsWith(p)
  );
  if (isGrowthPartner && !growthPartnerAllowedRoute) {
    return <Navigate to="/mi-panel" replace />;
  }

  // Admin: /home queda oculto; su entrada por default es el panel de marcas.
  if (isAdmin && location.pathname === '/home') {
    return <Navigate to="/admin/panel" replace />;
  }

  // Check if we're on Analytics20 route to remove padding
  const removePaddingRoutes = [
    '/analytics20',
    '/reporte-semanal',
    '/optimizador',
    '/home',
    '/campaigns',
    '/campaign-wizard',
    '/profile/brands',
    '/profile/connections',
    '/profile',
    '/profile/subscription',
    '/profile/payments',
    '/profile/privacy',
    '/profile/support',
    '/profile/users',
    '/objetivos',
    '/reporting',
    '/ads-age',
    '/ads-performance',
    '/__meta-creative-ranking',
    '/admin/bebo'
  ];

  const removePadding = removePaddingRoutes.some(route => location.pathname.includes(route));
  // Bebo IA is a full-bleed black immersive page: solid black shell, no main padding.
  const isBebo = location.pathname.startsWith('/admin/bebo');
  // En el panel del admin la barra superior la provee el header glass del propio
  // panel (marca + fechas + subsecciones, como el GP); ahí no va el selector flotante.
  const isAdminPanel = isAdmin && location.pathname.startsWith('/admin/panel');

  return (
    <TourProvider>
      <HelpCenterProvider>
        <MobileFilterProvider>
          <MobileTopNavBarProvider>
            <div
              className={`flex h-screen overflow-hidden ${isBebo ? 'bg-[#0a0c0e] app-dark-input-scope' : removePadding ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 app-dark-input-scope' : 'bg-white'}`}
              style={{
                '--brand-selector-bottom': '4.5rem',
                '--sidebar-width': `${effectiveSidebarWidth}px`,
              }}
            >
            {/* Tour guiado de la app */}
            <AppTour />

            {/* Selector de marcas flotante + slot para filtros (analytics) */}
            <TopBarLeftContentProvider>
              {!isGrowthPartner && !isBebo && !isAdminPanel && <FloatingBrandSelector />}
              {!isBebo && <MobileTopNavBarContent />}

        {/* Navegación: dock flotante para Growth Partner, sidebar para el resto */}
      {isGrowthPartner ? (
        <GrowthPartnerDock onNavigate={path => navigate(path)} />
      ) : isAdmin ? (
        <>
          {/* Desktop: dock vertical glass (nav + cuenta). La barra superior la
              provee el header glass de cada página (panel). Mobile: Sidebar (drawer). */}
          <div className="hidden lg:block">
            <AdminDock onNavigate={path => navigate(path)} dark={isBebo || removePadding} />
          </div>
          <div className="lg:hidden">
            <Sidebar
              onExpandedChange={() => {}}
              onNavigate={path => navigate(path)}
              showMobileFooterMenu={true}
              onSidebarStateChange={handleSidebarStateChange}
            />
          </div>
        </>
      ) : (
        <Sidebar
          campaigns={campaigns}
          selectedCampaign={null}
          setSelectedCampaign={() => {}}
          setIsNewCampaignModalOpen={() => {}}
          onExpandedChange={() => {}}
          onNavigate={path => navigate(path)}
          showMobileFooterMenu={true}
          onSidebarStateChange={handleSidebarStateChange}
        />
      )}

      {/* Main content with side padding for vertical sidebar */}
      <main
        className={`flex-1 overflow-auto transition-all duration-500 ease-in-out ${isBebo ? '' : 'pt-16 pb-20 lg:pt-[5.5rem] lg:pb-4'} ${removePadding ? '' : 'p-4'} ${isAdmin ? 'lg:ml-[100px]' : ''}`}
        style={isAdmin ? {} : { marginLeft: `${sidebarMargin}px`, '--sidebar-width': `${sidebarMargin}px` }}
      >
        <AnalyticsFilterProvider>
          <Outlet context={{ campaigns, loading, error }} />
        </AnalyticsFilterProvider>
      </main>
            </TopBarLeftContentProvider>

            </div>
          </MobileTopNavBarProvider>
        </MobileFilterProvider>
      </HelpCenterProvider>
    </TourProvider>
  );
};

// Componente interno que consume el context
const MobileTopNavBarContent = () => {
  const { config } = useMobileTopNavBar();
  return <MobileTopNavBar {...config} />;
};

export default ProtectedLayout;
